import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { BuffOrDebuff } from '../../types';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { getBuffName, formatBuffEffect, getBuffSourceDescription } from '../../utils/formatting';
import { ItemDetailsPopover } from '../shared/ItemDetailsPopover';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { SectionHeading } from '../shared/SectionHeading';
import { WarbandBuffsModal } from '../modals/WarbandBuffsModal';
import { PopoverState } from '../../hooks/useItemPopover';

export const WarbandBuffsPanel: React.FC = () => {
  const { warbandBuffs, wordEffects, selectedWarbandId } = useWarbandStore();
  const updateWarbandBuffs = useDirtyTrackingStore((s) => s.updateWarbandBuffs);
  const updateWordEffects = useDirtyTrackingStore((s) => s.updateWordEffects);

  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [buffPopover, setBuffPopover] = React.useState<PopoverState | null>(null);
  const [buffPendingDelete, setBuffPendingDelete] = React.useState<BuffOrDebuff | null>(null);
  const [stackChooseOpen, setStackChooseOpen] = React.useState(false);

  const handleBuffChipClick = async (e: React.MouseEvent<HTMLElement>, buff: BuffOrDebuff) => {
    setBuffPopover({
      anchor: e.currentTarget,
      name: getBuffName(buff),
      description: 'Loading…',
      modifiers: formatBuffEffect(buff),
      tags: [],
    });
    if (selectedWarbandId) {
      const desc = await getBuffSourceDescription(selectedWarbandId, buff);
      setBuffPopover((prev) => (prev ? { ...prev, description: desc || null } : null));
    }
  };

  const handleDeleteChip = (buff: BuffOrDebuff) => {
    setBuffPendingDelete(buff);
    if ((buff.stackCount ?? 1) > 1) {
      setStackChooseOpen(true);
    }
  };

  const handleCancelDelete = () => {
    setBuffPendingDelete(null);
    setStackChooseOpen(false);
  };

  const removeOneStack = () => {
    if (!buffPendingDelete) return;
    const allIds = warbandBuffs.flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
    const idx = allIds.lastIndexOf(buffPendingDelete.id);
    const remaining = [...allIds.slice(0, idx), ...allIds.slice(idx + 1)];
    updateWarbandBuffs(remaining);
    setBuffPendingDelete(null);
    setStackChooseOpen(false);
  };

  const removeAllStacks = () => {
    if (!buffPendingDelete) return;
    const remaining = warbandBuffs
      .filter((b) => b.id !== buffPendingDelete.id)
      .flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
    updateWarbandBuffs(remaining);
    // If it was a word spell buff, remove its target too
    if (buffPendingDelete.id === 'spell_power_word' || buffPendingDelete.id === 'spell_draining_word') {
      updateWordEffects(wordEffects.filter((e) => e.buffId !== buffPendingDelete.id));
    }
    setBuffPendingDelete(null);
    setStackChooseOpen(false);
  };

  const stackCount = buffPendingDelete?.stackCount ?? 1;
  const buffName = buffPendingDelete ? getBuffName(buffPendingDelete) : '';

  return (
    <>
      <Box>
        <SectionHeading
          label="Warband Effects"
          action={
            <IconButton size="small" onClick={() => setAddModalOpen(true)}>
              <AddIcon />
            </IconButton>
          }
        />

        {warbandBuffs.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {warbandBuffs.map((buff) => {
              const count = buff.stackCount ?? 1;
              const baseName = count > 1 ? `${getBuffName(buff)} ×${count}` : getBuffName(buff);
              const wordEffect = (buff.id === 'spell_power_word' || buff.id === 'spell_draining_word')
                ? wordEffects.find((e) => e.buffId === buff.id)
                : undefined;
              const label = wordEffect ? `${baseName} \u2192 ${wordEffect.targetSpellName}` : baseName;
              return (
                <Chip
                  key={buff.id}
                  size="small"
                  label={label}
                  onClick={(e) => handleBuffChipClick(e, buff)}
                  onDelete={() => handleDeleteChip(buff)}
                />
              );
            })}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            No warband-wide effects active.
          </Typography>
        )}
      </Box>

      <WarbandBuffsModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />

      <ItemDetailsPopover popover={buffPopover} onClose={() => setBuffPopover(null)} />

      {/* Single-stack removal confirm */}
      <ConfirmActionDialog
        open={buffPendingDelete !== null && stackCount === 1}
        title="Remove warband effect"
        message={`Remove "${buffName}" from the whole warband?`}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={handleCancelDelete}
        onConfirm={removeAllStacks}
      />

      {/* Multi-stack removal: custom dialog with three choices */}
      <Dialog open={stackChooseOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Remove warband effect</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`"${buffName}" has ${stackCount} stacks. How many would you like to remove?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={removeOneStack}>One</Button>
          <Button variant="contained" color="error" onClick={removeAllStacks}>All</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

